import React, { useEffect, useState } from 'react';
import { RentalForm } from './components/RentalForm';
import { RentalSummary } from './components/RentalSummary';
import { Logo } from './components/Logo';
import { DiscountBanner } from './components/DiscountBanner';
import { PendingRentals } from './components/PendingRentals';
import { usePrices } from './contexts/PriceContext';
import { useEmail } from './contexts/EmailContext';
import { Link, useNavigate } from 'react-router-dom';
import { SettingsIcon, ShieldCheck, PhoneIcon, MailIcon } from 'lucide-react';
export function App() {
  const {
    prices
  } = usePrices();
  const {
    email
  } = useEmail();
  const navigate = useNavigate();
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [tapCount, setTapCount] = useState(0);
  const [tapTimer, setTapTimer] = useState<NodeJS.Timeout | null>(null);
  const handleAdminClick = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      const password = prompt('Enter admin password:');
      if (password === '2026gen3') {
        navigate('/admin');
      } else if (password !== null) {
        alert('Incorrect password');
      }
    } else {
      const button = e.currentTarget as HTMLElement;
      button.classList.add('shake');
      setTimeout(() => button.classList.remove('shake'), 500);
    }
  };
  const handleAdminTouch = () => {
    const newCount = tapCount + 1;
    setTapCount(newCount);
    // Clear existing timer
    if (tapTimer) {
      clearTimeout(tapTimer);
    }
    // If triple tap, show password prompt
    if (newCount === 3) {
      setTapCount(0);
      setTapTimer(null);
      const password = prompt('Enter admin password:');
      if (password === '2026gen3') {
        navigate('/admin');
      } else if (password !== null) {
        alert('Incorrect password');
      }
    } else {
      // Reset tap count after 500ms
      const timer = setTimeout(() => {
        setTapCount(0);
      }, 500);
      setTapTimer(timer);
    }
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchEnd(null);
    setIsLongPress(false);
    // Start long press timer (500ms)
    const timer = setTimeout(() => {
      setIsLongPress(true);
    }, 500);
    setLongPressTimer(timer);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isLongPress) {
      // Cancel long press if user moves before timer completes
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      return;
    }
    // Prevent page refresh during drag
    e.preventDefault();
    const touch = e.touches[0];
    setTouchEnd({
      x: touch.clientX,
      y: touch.clientY
    });
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    // Clear timer if still running
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    if (!touchStart || !touchEnd || !isLongPress) {
      setTouchStart(null);
      setTouchEnd(null);
      setIsLongPress(false);
      return;
    }
    const deltaY = touchEnd.y - touchStart.y;
    const deltaX = Math.abs(touchEnd.x - touchStart.x);
    // Check if it's a downward drag (deltaY > 30px) and mostly vertical (deltaX < 30px)
    if (deltaY > 30 && deltaX < 30) {
      e.preventDefault();
      const password = prompt('Enter admin password:');
      if (password === '2026gen3') {
        navigate('/admin');
      } else if (password !== null) {
        alert('Incorrect password');
      }
    }
    setTouchStart(null);
    setTouchEnd(null);
    setIsLongPress(false);
  };
  const initialSelections = {
    gen3Body: 0,
    gen3Head: 0,
    gen3JudgesOnly: 0,
    fullGen3Ring: 0,
    requireTech: false,
    numberOfDays: 1
  };
  const [selections, setSelections] = useState(initialSelections);
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [isCaliforniaAddress, setIsCaliforniaAddress] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandPendingRentals, setExpandPendingRentals] = useState(false);
  const [discountSelections, setDiscountSelections] = useState({
    collegiate: false,
    usatSanctioned: false
  });
  const [techOnlyMode, setTechOnlyMode] = useState(false);
  const [isGen2Mode, setIsGen2Mode] = useState(false);
  const handleGen2Toggle = () => {
    const newMode = !isGen2Mode;
    setIsGen2Mode(newMode);
    if (newMode) {
      // Gen 2 cannot use any discounts - disable both
      setDiscountSelections({
        usatSanctioned: false,
        collegiate: false
      });
    }
  };
  const handleTechOnlyToggle = () => {
    const newMode = !techOnlyMode;
    setTechOnlyMode(newMode);
    if (newMode) {
      // Enable tech-only: clear all equipment, auto-check technician
      setSelections({
        gen3Body: 0,
        gen3Head: 0,
        gen3JudgesOnly: 0,
        fullGen3Ring: 0,
        requireTech: true,
        numberOfDays: selections.numberOfDays
      });
    } else {
      // Disable tech-only: uncheck technician, let user re-select
      setSelections(prev => ({
        ...prev,
        requireTech: false
      }));
    }
  };
  useEffect(() => {
    // Apply 30% discount for Gen 2 equipment
    const equipmentMultiplier = isGen2Mode ? 0.7 : 1.0;
    const bodyPrice = selections.gen3Body * prices.gen3Body * equipmentMultiplier;
    const headPrice = selections.gen3Head * prices.gen3Head * equipmentMultiplier;
    const judgesPrice = selections.gen3JudgesOnly * prices.gen3JudgesOnly * equipmentMultiplier;
    const fullRingPrice = selections.fullGen3Ring * prices.fullGen3Ring * equipmentMultiplier;
    // Calculate tech fee (multiplied by number of days) - NOT affected by Gen 2 discount
    const techPrice = selections.requireTech ? prices.technician * selections.numberOfDays : 0;
    // Equipment subtotal for Day 1
    const equipmentSubtotal = bodyPrice + headPrice + judgesPrice + fullRingPrice;
    // If multi-day event, add additional days at 50% price
    const additionalDaysPrice = selections.numberOfDays > 1 ? equipmentSubtotal * 0.5 * (selections.numberOfDays - 1) : 0;
    // Total subtotal (Day 1 + additional days + tech fee)
    const calculatedSubtotal = equipmentSubtotal + additionalDaysPrice + techPrice;
    setSubtotal(calculatedSubtotal);
    // Calculate discount ONLY on equipment (not tech fee)
    const totalEquipment = equipmentSubtotal + additionalDaysPrice;
    let discountAmount = 0;
    if (discountSelections.usatSanctioned && !isGen2Mode) {
      discountAmount = totalEquipment * 0.15; // 15% for USAT on equipment only
    } else if (discountSelections.collegiate && !isGen2Mode) {
      discountAmount = totalEquipment * 0.1; // 10% for Collegiate on equipment only
    }
    setDiscount(discountAmount);
    // Apply discount before tax
    const subtotalAfterDiscount = calculatedSubtotal - discountAmount;
    const calculatedTax = isCaliforniaAddress ? subtotalAfterDiscount * prices.taxRate : 0;
    setTax(calculatedTax);
    setTotal(subtotalAfterDiscount + calculatedTax);
  }, [selections, isCaliforniaAddress, prices, discountSelections, isGen2Mode]);
  const handleSelectionChange = (name: string, value: number | boolean) => {
    // Block equipment changes in tech-only mode
    if (techOnlyMode && name !== 'numberOfDays') return;
    setSelections(prev => {
      if (name === 'gen3Head' && typeof value === 'number') {
        const totalBodies = prev.gen3Body + prev.fullGen3Ring;
        if (value > totalBodies) {
          return prev;
        }
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };
  const handleTechToggle = () => {
    // In tech-only mode, don't allow unchecking tech
    if (techOnlyMode) return;
    setSelections(prev => ({
      ...prev,
      requireTech: !prev.requireTech
    }));
  };
  const handleTwoDayToggle = (isTwoDay: boolean) => {
    setSelections(prev => ({
      ...prev,
      isTwoDayEvent: isTwoDay
    }));
  };
  const handleCaliforniaAddressChange = (isCaliforniaAddress: boolean) => {
    setIsCaliforniaAddress(isCaliforniaAddress);
  };
  const handleDiscountToggle = (discountType: string) => {
    // Block all discounts in Gen 2 mode
    if (isGen2Mode) return;
    setDiscountSelections(prev => ({
      ...prev,
      [discountType]: !prev[discountType]
    }));
  };
  const handleNumberOfDaysChange = (days: number) => {
    setSelections(prev => ({
      ...prev,
      numberOfDays: Math.max(1, Math.min(7, days))
    }));
  };
  const handleRentalSubmitted = () => {
    setRefreshKey(prev => prev + 1);
    setSelections(initialSelections);
    setIsCaliforniaAddress(false);
    setDiscountSelections({
      collegiate: false,
      usatSanctioned: false
    });
    setTechOnlyMode(false);
    setIsGen2Mode(false);
    setExpandPendingRentals(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-slate-100" data-id="element-0">
      <style data-id="element-1">{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>

      <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4" data-id="element-3">
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-6" data-id="element-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 flex-1 min-w-0" data-id="element-5">
              <div className="text-slate-700 text-xs sm:text-sm" data-id="element-6">
                <div className="flex items-center mb-1" data-id="element-7">
                  <PhoneIcon size={14} className="mr-2 text-red-600 flex-shrink-0" data-id="element-8" />
                  <span className="font-medium" data-id="element-9">(510) 556-5516</span>
                </div>
                <div className="flex items-center" data-id="element-10">
                  <MailIcon size={14} className="mr-2 text-red-600 flex-shrink-0" data-id="element-11" />
                  <span className="font-medium break-all" data-id="element-12">
                    sales@truescore.com
                  </span>
                </div>
              </div>
              <div className="text-slate-700 text-xs sm:text-sm" data-id="element-13">
                <div className="flex items-center mb-1" data-id="element-14">
                  <PhoneIcon size={14} className="mr-2 text-red-600 flex-shrink-0" data-id="element-15" />
                  <span className="font-medium" data-id="element-16">(517) 294-0315</span>
                </div>
                <div className="flex items-center" data-id="element-17">
                  <MailIcon size={14} className="mr-2 text-red-600 flex-shrink-0" data-id="element-18" />
                  <span className="font-medium break-all" data-id="element-19">
                    shane@tkdscore.com
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-id="element-20">
              <a href="https://www.truescore.com/purchase" target="_blank" rel="noopener noreferrer" className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-xs sm:text-sm whitespace-nowrap flex items-center gap-2" data-id="element-21">
                BUY GEN3
              </a>

              <button onClick={handleAdminClick} onTouchEnd={handleAdminTouch} className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0" title="Shift+Click or triple-tap to access admin panel" data-id="element-22">
                <ShieldCheck size={18} className="text-slate-400 hover:text-blue-600 transition-colors" data-id="element-23" />
              </button>

              {email === 'shane@tkdscore.com' && <Link to="/config" className="flex items-center text-slate-600 hover:text-blue-800 text-sm flex-shrink-0" title="Configure Prices" data-id="element-24">
                  <SettingsIcon size={16} className="mr-1" data-id="element-25" />
                  <span className="hidden sm:inline" data-id="element-26">Configure Prices</span>
                </Link>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6" data-id="element-27">
        <header className="mb-6 sm:mb-8 mt-4 sm:mt-6" data-id="element-28">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" data-id="element-29">
            {isGen2Mode ? 'Gen 2' : 'Gen 3'} Scoring System Rental
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base" data-id="element-30">
            Select the components you need for your event
          </p>
        </header>

        <div className="flex flex-col lg:flex-row gap-6 mb-8" data-id="element-31">
          <div className="flex-shrink-0 lg:w-auto w-full flex justify-center lg:justify-start" data-id="element-32">
            <img src="https://cdn.magicpatterns.com/uploads/jAaJbUwB1t1dUCrFYJwyZT/Unknown_9.jpg" alt="TrueScore & USA TKD" className="h-auto w-full lg:w-auto object-contain" style={{
            maxWidth: '300px'
          }} data-id="element-33" />
          </div>

          <div className="flex-1 space-y-6 min-w-0" data-id="element-34">
            <PendingRentals refreshKey={refreshKey} shouldExpand={expandPendingRentals} onExpandChange={setExpandPendingRentals} data-id="element-35" />

            <DiscountBanner discountSelections={discountSelections} onDiscountToggle={handleDiscountToggle} isGen2Mode={isGen2Mode} data-id="element-36" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-37">
          <div className="lg:col-span-2" data-id="element-38">
            <RentalForm selections={selections} onSelectionChange={handleSelectionChange} onTechToggle={handleTechToggle} onNumberOfDaysChange={handleNumberOfDaysChange} prices={prices} techOnlyMode={techOnlyMode} onTechOnlyToggle={handleTechOnlyToggle} isGen2Mode={isGen2Mode} onGen2Toggle={handleGen2Toggle} data-id="element-39" />
          </div>
          <div className="lg:col-span-1" data-id="element-40">
            <RentalSummary selections={selections} subtotal={subtotal} discount={discount} tax={tax} total={total} onCaliforniaAddressChange={handleCaliforniaAddressChange} prices={prices} onRentalSubmitted={handleRentalSubmitted} discountSelections={discountSelections} isGen2Mode={isGen2Mode} data-id="element-41" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12" data-id="element-42">
        <div className="max-w-6xl mx-auto px-6 py-6" data-id="element-43">
          <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-500 mb-4" data-id="element-44">
            <button onClick={() => {
            alert('Privacy Policy\n\nTruescore and TKDScore ("we," "our," or "us") respect your privacy. We collect only the information necessary to process your rental requests and provide our services. This includes your name, email address, phone number, event details, and payment information.\n\nWe use this information solely to:\n• Process and fulfill your equipment rental orders\n• Communicate with you about your rental\n• Improve our services\n\nWe do not sell, rent, or share your personal information with third parties for marketing purposes. Your payment information is processed securely through our payment processor and is not stored on our servers.\n\nWe retain your information only as long as necessary to provide our services and comply with legal obligations. You may request access to, correction of, or deletion of your personal information by contacting us at shane@tkdscore.com.\n\nBy using our services, you consent to this privacy policy.');
          }} className="hover:text-slate-700 transition-colors" data-id="element-45">
              Privacy Policy
            </button>
            <span className="text-slate-300" data-id="element-46">•</span>
            <button onClick={() => {
            alert('Security Policy\n\nTruescore and TKDScore are committed to protecting your personal information. We do not sell or share your personal information with third parties. All data is transmitted securely using industry-standard encryption, and payment processing is handled by secure, PCI-compliant payment processors.');
          }} className="hover:text-slate-700 transition-colors" data-id="element-47">
              Security Policy
            </button>
            <span className="text-slate-300" data-id="element-48">•</span>
            <button onClick={() => {
            alert('Refund Policy\n\nTruescore and TKDScore do not offer refunds within 90 days of your scheduled event date. Cancellations made more than 90 days before your event may be eligible for a refund. Please contact us at shane@tkdscore.com for any questions regarding cancellations or refunds.');
          }} className="hover:text-slate-700 transition-colors" data-id="element-49">
              Refund Policy
            </button>
          </div>

          <div className="text-center mb-3 text-xs text-slate-500" data-id="element-50">
            <div className="font-medium" data-id="element-51">TrueScore/CnoWire Inc</div>
            <div className="text-slate-400" data-id="element-52">(Impact Measurements Inc)</div>
            <div className="mt-1" data-id="element-53">1425 W San Carlos, San Jose, CA 95126</div>
          </div>

          <div className="text-center text-xs text-slate-400" data-id="element-54">
            © {new Date().getFullYear()} Truescore and TKDScore. All rights
            reserved.
          </div>
        </div>
      </footer>
    </div>;
}